/*
 *  These constants have specific values that allow percentage columns in a table to be sorted
 *  correctly. The lowest value we could possibly have for a percentage is -100. Setting
 *  NOT_ENOUGH_DATA to -101 makes values without enough data fall below the data we actually
 *  want to show. Setting NO_DATA to -102 makes values with no data (usually null values)
 *  fall below the NOT_ENOUGH_DATA values.
 *
 *  When sorting compliance table data, we need to sort not enough data rows to the bottom, and then
 *  sort these rows by compliance percentage. We add or subtract 201 from the compliance percentage
 *  of a not enough data row to ensure proper sorting.
 *
 *  Additional context:
 *  When sorting a list of numbers that contains null values, null gets cast to 0. So in a list of
 *  [null, -5, 7], null would show up in between -5 and 7, but we want null values to go below the
 *  lowest actual values.
 */
const GRAPHQL_DATE_FORMAT = "YYYY-MM-DD"
const NO_DATA_BLURB = "No data found. Try a new search or date range."
const NOT_ENOUGH_DATA_SORT_OFFSET = 201

const ENTITY_TYPE_MAPPINGS = [
  {
    singularDisplay: "Facility",
    pluralDisplay: "Facilities",
    singularShortDisplay: "Facility",
    pluralShortDisplay: "Facilities",
    singularField: "facility",
    pluralField: "facilities",
    singularRoute: "facility",
    pluralRoute: "facilities",
    __typename: "Facility",
  },
  {
    singularDisplay: "Department",
    pluralDisplay: "Departments",
    singularShortDisplay: "Dept",
    pluralShortDisplay: "Depts",
    singularField: "department",
    pluralField: "departments",
    singularRoute: "department",
    pluralRoute: "departments",
    __typename: "Department",
  },
  {
    singularDisplay: "Unit",
    pluralDisplay: "Units",
    singularShortDisplay: "Unit",
    pluralShortDisplay: "Units",
    singularField: "unit",
    pluralField: "units",
    singularRoute: "unit",
    pluralRoute: "units",
    __typename: "Unit",
  },
  {
    singularDisplay: "Patient Room",
    pluralDisplay: "Patient Rooms",
    singularShortDisplay: "Patient Room",
    pluralShortDisplay: "Patient Rooms",
    singularField: "location",
    pluralField: "locations",
    singularRoute: "room",
    pluralRoute: "rooms",
    __typename: "Location",
  },
  {
    singularDisplay: "Staff",
    pluralDisplay: "Staff",
    singularShortDisplay: "Staff",
    pluralShortDisplay: "Staff",
    singularField: "user",
    pluralField: "users",
    singularRoute: "staff",
    pluralRoute: "staff",
    __typename: "User",
  },
  {
    singularDisplay: "Role",
    pluralDisplay: "Roles",
    singularShortDisplay: "Role",
    pluralShortDisplay: "Roles",
    singularField: "healthcareRole",
    pluralField: "healthcareRoles",
    singularRoute: "role",
    pluralRoute: "roles",
    __typename: "Role",
  },
]

// { singularField: [pluralField] }
const POSSIBLE_CHILD_ENTITIES = {
  department: ["users", "healthcareRoles", "units"],
  unit: ["users", "healthcareRoles", "locations"],
  healthcareRole: ["users"],
}

const DEFAULT_CHILD_ENTITIES = {
  department: "users",
  unit: "locations",
  healthcareRole: "users",
}

const SECONDARY_NAV_TABS = {
  department: [
    { key: "overview", label: "Overview", url: "overview" },
    { key: "staff", label: "Staff", url: "staff" },
    { key: "roles", label: "Roles", url: "roles" },
    { key: "units", label: "Units", url: "units" },
  ],
  unit: [
    { key: "overview", label: "Overview", url: "overview" },
    { key: "rooms", label: "Rooms", url: "rooms" },
  ],
  healthcareRole: [{ key: "staff", label: "Staff", url: "staff" }],
}

const VISIT_FLAG_REASONS = {
  never_occurred: "Visit(s) never occurred",
  incorrect_compliance: "Incorrect compliance recorded",
  incorrect_hygiene_method:
    "Incorrect hygiene method recorded (soap/sanitizer)",
  wrong_room: "Visits are correct but recorded in the wrong room",
  other: "Other",
}

const NR_MIN_OPPORTUNITIES = 50
const BSR_MIN_OPPORTUNITIES = 0

const RM_WARNING_THRESHOLD_MINUTES = 20
const RM_SUCCESS = "success-rounding"
const RM_WARNING = "warning-rounding"
const RM_DANGER = "danger-rounding"

export {
  ENTITY_TYPE_MAPPINGS,
  GRAPHQL_DATE_FORMAT,
  NO_DATA_BLURB,
  NOT_ENOUGH_DATA_SORT_OFFSET,
  POSSIBLE_CHILD_ENTITIES,
  DEFAULT_CHILD_ENTITIES,
  SECONDARY_NAV_TABS,
  VISIT_FLAG_REASONS,
  NR_MIN_OPPORTUNITIES,
  BSR_MIN_OPPORTUNITIES,
  RM_WARNING_THRESHOLD_MINUTES,
  RM_WARNING,
  RM_SUCCESS,
  RM_DANGER,
}
